<template>
  <div class="table-responsive">
    <table class="table table-sm table-hover">
      <thead>
        <tr>
          <th>{{ $t("common.id") }}</th>
          <th>{{ $t("dashboard.referrer_company_id") }}</th>
          <th>{{ $t("dashboard.utm_source") }}</th>
          <th>{{ $t("dashboard.utm_medium") }}</th>
          <th>{{ $t("dashboard.utm_term") }}</th>
          <th>{{ $t("dashboard.utm_content") }}</th>
          <th>{{ $t("dashboard.utm_campaign") }}</th>
          <th>{{ $t("dashboard.created_at") }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="tracking in trackings" :key="tracking.id">
          <td>{{ tracking.id }}</td>
          <td>
            <div class="d-flex gap-2">
              <input
                type="text"
                :value="referrerOf(tracking)"
                class="form-control form-control-sm"
                @input="
                  editedReferrers.set(
                    tracking.id,
                    ($event.target as HTMLInputElement).value
                  )
                "
              />
              <button
                class="btn btn-sm btn-primary"
                @click="updateTracking(tracking)"
              >
                Save
              </button>
            </div>
          </td>
          <td>{{ tracking.utm_source }}</td>
          <td>{{ tracking.utm_medium }}</td>
          <td>{{ tracking.utm_term }}</td>
          <td>{{ tracking.utm_content }}</td>
          <td>{{ tracking.utm_campaign }}</td>
          <td>{{ tracking.created_at }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { useAccountStore } from "@/composables/account/useAccountStore";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { ConversionTracking } from "@/types";
import { apiErrorLogFields } from "@/utils/apiErrors";
import { ref, watch } from "vue";

/** What `POST /api/admin/tracking/{id}` answers with: the tracking as stored. */
interface TrackingUpdateAnswer {
  tracking: { id: number; referrer_id: number | null };
}

const props = defineProps<{
  trackings: readonly ConversionTracking[];
}>();

const { globalErrorHandler } = useGlobalHandlers();
const accountStore = useAccountStore();

// Only the referrers typed but not saved, by tracking id: every other row shows the server's, so the
// reload after one row's save leaves another row's edit alone.
const editedReferrers = ref(new Map<number, string>());
// The referrers saves stored, by tracking id, shown until the store takes another answer: its copy of
// the row predates the save until then, and the reread after the save can fail.
const savedReferrers = ref(new Map<number, string>());

watch(
  () => props.trackings,
  () => savedReferrers.value.clear()
);

const referrerOf = (tracking: ConversionTracking) =>
  editedReferrers.value.get(tracking.id) ??
  savedReferrers.value.get(tracking.id) ??
  referrerText(tracking.referrer_id);

async function updateTracking(tracking: ConversionTracking) {
  const referrerId = referrerOf(tracking);
  const confirmed = window.confirm(
    `Are you sure you want to update tracking id ${tracking.id} to have referrer ${referrerId}?`
  );
  if (!confirmed) return;
  try {
    const { data } = await goSolidApi.post<TrackingUpdateAnswer>(
      `/api/admin/tracking/${tracking.id}`,
      { referrer_id: referrerId }
    );
    // Saved: the row shows the referrer as the server stored it.
    editedReferrers.value.delete(tracking.id);
    savedReferrers.value.set(
      tracking.id,
      referrerText(data.tracking.referrer_id)
    );
  } catch (error: unknown) {
    globalErrorHandler(error);
    return;
  }
  // The save is committed either way, so a failed reread only leaves the row on the save's answer
  // until the store's next load retries.
  await accountStore.reload().catch((error: unknown) =>
    console.error("Account refresh after a tracking save failed", {
      trackingId: tracking.id,
      ...apiErrorLogFields(error),
    })
  );
}

function referrerText(referrerId: number | null | undefined): string {
  return String(referrerId ?? "");
}
</script>
